import { History, Home, Plus, Search, User } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'

import { useAuth } from '@/features/auth/hooks/useAuth'
import { CreateSessionLink } from '@/features/sessions/components/CreateSessionLink'

import { AccountMenu } from './AccountMenu'
import { pickActivePath } from './pickActivePath'

type IconComp = typeof Home

interface NavItem {
  to: string
  labelKey: string
  Icon: IconComp
  /** Si es el botón "+" central — se renderiza distinto (flotante, círculo rojo) */
  center?: boolean
}

/**
 * Items navegables del tab bar. El item "Cuenta" se trata aparte porque
 * es un trigger de overlay (AccountMenu), no un Link.
 */
const NAV_ITEMS: NavItem[] = [
  { to: '/', labelKey: 'landing.tabBar.home', Icon: Home },
  { to: '/sessions', labelKey: 'landing.tabBar.explore', Icon: Search },
  { to: '/sessions/new', labelKey: 'landing.tabBar.create', Icon: Plus, center: true },
  { to: '/sessions/mine', labelKey: 'landing.tabBar.mine', Icon: History },
]

/**
 * Tab bar inferior global — visible en TODAS las pantallas (`md:hidden`).
 *
 * 5 items: Inicio · Explorar · (+) Crear · Mis partidas · Cuenta.
 * El item "Cuenta" abre `AccountMenu` (sheet brutal) en lugar de navegar.
 *
 * En estado GUEST, los enlaces protegidos disparan ProtectedRoute
 * aguas abajo (redirige a /login). Este componente NO checkea auth — el
 * AccountMenu sí, para mostrar items diferentes.
 */
// Una cuenta TIENDA nunca crea ni se une a partidas (bloqueado 403 en
// backend) — se ocultan sus tabs "Crear" y "Mis partidas" del jugador.
const PLAYER_ONLY_PATHS = new Set(['/sessions/new', '/sessions/mine'])

export function MobileTabBar() {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const { user } = useAuth()
  const [accountOpen, setAccountOpen] = useState(false)

  const items =
    user?.role === 'TIENDA' ? NAV_ITEMS.filter((i) => !PLAYER_ONLY_PATHS.has(i.to)) : NAV_ITEMS

  const navPaths = items.map((i) => i.to)
  const activePath = pickActivePath(pathname, navPaths)

  return (
    <>
      <nav
        aria-label={t('landing.tabBar.ariaLabel')}
        className="sticky bottom-0 z-30 bg-transparent px-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-2 md:hidden"
      >
        <div className="brutal-lg flex items-center justify-around rounded-2xl bg-background p-2">
          {items.map((item) => (
            <TabBarLink
              key={item.to}
              item={item}
              isActive={activePath === item.to}
              label={t(item.labelKey)}
            />
          ))}
          <TabBarAccountTrigger
            isActive={accountOpen}
            label={t('landing.tabBar.account')}
            onOpen={() => setAccountOpen(true)}
          />
        </div>
      </nav>

      {accountOpen && <AccountMenu onClose={() => setAccountOpen(false)} />}
    </>
  )
}

// ---------------------------------------------------------------------------
// TabBarLink · item navegable (Inicio / Explorar / Crear / Mis partidas)
// ---------------------------------------------------------------------------

interface TabBarLinkProps {
  item: NavItem
  isActive: boolean
  label: string
}

function TabBarLink({ item, isActive, label }: TabBarLinkProps) {
  const { Icon, to, center } = item

  if (center) {
    // Etiqueta VISIBLE bajo el "+", como sus hermanos. Un "+" mudo era
    // ambiguo y la gente no descubría que podía crear partidas.
    return (
      <CreateSessionLink to={to} className="-mt-5 flex min-w-0 flex-1 flex-col items-center gap-2">
        <span className="brutal-sm flex size-12 items-center justify-center rounded-xl bg-red text-background">
          <Icon size={22} strokeWidth={3} aria-hidden="true" />
        </span>
        <span className="w-full truncate text-center text-[10px] font-bold uppercase leading-none tracking-wider text-red">
          {label}
        </span>
      </CreateSessionLink>
    )
  }

  return (
    <Link
      to={to}
      aria-current={isActive ? 'page' : undefined}
      onClick={(e) => {
        // Tab activa = no-op para no pisar filtros/tab actuales (ej. en
        // /sessions un PUSH "limpio" borraría la query con los filtros).
        if (isActive) e.preventDefault()
      }}
      className={`flex min-h-[44px] min-w-0 flex-1 flex-col items-center justify-center gap-0.5 ${
        isActive ? 'text-red' : 'text-muted-foreground'
      }`}
    >
      <Icon size={22} strokeWidth={isActive ? 2.5 : 2.2} aria-hidden="true" />
      <span className="w-full truncate text-center text-[10px] font-bold uppercase tracking-wider">
        {label}
      </span>
    </Link>
  )
}

// ---------------------------------------------------------------------------
// TabBarAccountTrigger · botón que abre el AccountMenu (sheet)
// ---------------------------------------------------------------------------

interface TabBarAccountTriggerProps {
  isActive: boolean
  label: string
  onOpen: () => void
}

function TabBarAccountTrigger({ isActive, label, onOpen }: TabBarAccountTriggerProps) {
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-expanded={isActive}
      aria-haspopup="dialog"
      className={`flex min-h-[44px] min-w-0 flex-1 flex-col items-center justify-center gap-0.5 ${
        isActive ? 'text-red' : 'text-muted-foreground'
      }`}
    >
      <User size={22} strokeWidth={isActive ? 2.5 : 2.2} aria-hidden="true" />
      <span className="w-full truncate text-center text-[10px] font-bold uppercase tracking-wider">
        {label}
      </span>
    </button>
  )
}
